import React, { useState } from 'react';
import { ModalStatusType } from '../../../types/common';
import { Form, Formik } from 'formik';
import ModalComponent from '../../../components/modal/modal';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import { YesNoBoolean } from '../../../constants/common';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import _ from 'lodash';
import {
  useGetDestinationCompanies,
  useGetRoleOptions,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import { ROLE_PUBLISH_VALIDATION } from '../../../utils/validations/validations';
import { RolePublishedToCompanyService } from '../../../services/wealthlane-identity-services';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';

interface RolePublishModalType {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  modalLoading?: boolean;
  setModalStatus: (value: ModalStatusType) => void;
  initialData: {
    data: any;
    id: string;
  };
  rowData?: any;
  id?: string;
}

const RolePublishModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  initialData,
  modalLoading,
  setModalStatus,
  id
}: RolePublishModalType): JSX.Element => {
  const { handleRequest } = useWealthlaneCustomerApi();
  const { data: destinationCompaniesOption, isLoading: isDestinationCompaniesOptionLoading } =
    useGetDestinationCompanies(handleRequest);
  const { data: roleOption, isLoading: isRoleOptionLoading } = useGetRoleOptions(handleRequest);
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = (values: any): void => {
    setLoading(true);

    values?.publishId
      ? handleRequest(
        RolePublishedToCompanyService.patchApiIdentityApiAppRolePublishedToCompany(
          values?.publishId,
          values
        )
      )
        .then((data: any) => {
          if (data) {
            toast.success(<ToastComponent label='Success' message='Role Published edited.' />);
            queryClient.invalidateQueries('getRolePublishList');
            setLoading(false);
            closeModal();
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        })
      : handleRequest(
        RolePublishedToCompanyService.postApiIdentityApiAppRolePublishedToCompany(values)
      )
        .then((data: any) => {
          if (data) {
            toast.success(
              <ToastComponent label='Success' message='Role Published successfully.' />
            );
            queryClient.invalidateQueries('getRolePublishList');
            setLoading(false);
            closeModal();
          }
        })
        .catch((error: any) => {
          console.log('error', error);
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialData.data}
      validationSchema={ROLE_PUBLISH_VALIDATION}
      onSubmit={(values) => {
        handleSubmit(values);
      }}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            // loading={loading || loader}
            size='sm'
            id={id}
            title={`${modalStatus === 'Add' ? 'Create' : modalStatus} Role Publish`}
            valueEdited={_.isEqual(values, initialData.data)}
            children={
              <Form>
                <ModalBodyComponent>
                  <div className=''>
                    <FormSelect
                      id="destinationCompanyId"
                      type='text'
                      label={'Company'}
                      name='destinationCompanyId'
                      // viewName='messageTemplate'
                      containerClassName='formGroup'
                      onChange={(value: string) => {
                        setFieldValue('destinationCompanyId', value ?? null);
                      }}
                      errors={modalStatus === 'View' ? {} : errors}
                      touched={touched}
                      options={destinationCompaniesOption}
                      isLoading={isDestinationCompaniesOptionLoading}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isClearable
                      required
                    />
                    <FormSelect
                      id='sourceRoleId'
                      type='text'
                      label={'Role'}
                      name='sourceRoleId'
                      // viewName='messageTemplate'
                      containerClassName='formGroup'
                      onChange={(value: string) => {
                        setFieldValue('sourceRoleId', value ?? null);
                      }}
                      errors={modalStatus === 'View' ? {} : errors}
                      touched={touched}
                      options={roleOption}
                      isLoading={isRoleOptionLoading}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={modalStatus === 'View'}
                      isClearable
                      required
                    />
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  id='rolePublishModalFooter'
                  modalStatus={modalStatus}
                  loading={loading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData.data)}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default RolePublishModal;
